<script>
	import { onMount, onDestroy, tick } from 'svelte';
	import { fly } from 'svelte/transition';
	import { cubicOut } from 'svelte/easing';

	// import Soundbox from 'sound-box';

	import { db, giveClue, serverTimeOffsetMillis, arrayUnion } from './services/firebase-service';

	import g from './stores/game';

	import Layout from './layout.svelte';

	import ClueArea from './game/clue-area.svelte';
	import TeamList from './game/team-list.svelte';
	import Board from './game/board.svelte';
	import Clock from './components/clock.svelte';

	import { STATUSES, TEAMS, ACTIONS, TYPES } from './utils/constants';

	export let authID;
	export let gameID;
	export let game;
	export let gameRef;
	export let playersInput;
	export let authPlayerInput;

	$: gameOver = game.status >= STATUSES.finished;

	/*
		listen to gamesProtected
	*/
	let gameProtected;
	let unGamesProtected;
	let listeningToGameID;
	$: if (game && game.teamOne.giver && [game.teamOne.giver.id, game.teamTwo.giver.id].indexOf(authID) >= 0) {
		if (listeningToGameID !== gameID) {
			unGamesProtected && unGamesProtected();
			unGamesProtected = db.collection('gamesProtected').doc(gameID).onSnapshot(gameProtectedRef => {
				gameProtected = gameProtectedRef.data();
			});
			listeningToGameID = gameID;
		}
	}
	else {
		gameProtected = null;
	}

	$: console.log('gameProtected loaded', gameProtected);

	// cards
	$: cards = game && game.words && game.words.map((word, i) => {
		let team = TEAMS.neutral;
		let isCorrectGuess;
		let clue;

		if (game.wordsGuessed[i]) {
			isCorrectGuess = !!game.wordsGuessed[i].isCorrectGuess;
			clue = game.wordsGuessed[i].clue;
		}
		else {
			isCorrectGuess = null;
			clue = null;
		}

		if (gameProtected && gameProtected.cardsTeams) {
			team = gameProtected.cardsTeams[i];
		}
		else if (game.cardsTeams) {
			team = game.cardsTeams[i];
		}
		else if (game.wordsGuessed[i]) {
			team = game.wordsGuessed[i].team;
		}

		const isAuthTeam = team === authPlayer.team || !authPlayer.isGiver;
		const isInteresting = isAuthTeam || team === TEAMS.assasin;

		let isSelectable;

		if (gameOver) {
			isSelectable = true;
		}
		else if (isCorrectGuess === true || isCorrectGuess === false) {
			isSelectable = false;
		}
		else if (authPlayer.isTurn) {
			if (authPlayer.isGiver) {
				isSelectable = team === thisTurn.team;
			}
			else {
				isSelectable = team === TEAMS.neutral;
			}
		}
		else if (!authPlayer.isTurn) {
			isSelectable = true;
		}

		const isSelected = !!isSelectable && selectedCardsThisTurn.findIndex(card => card.word === word) >= 0;
		const isProcessingGuess = players.some(p => p.input && p.input.guessWords && p.input.guessWords.indexOf(word) >= 0) && isCorrectGuess !== true && isCorrectGuess !== false;

		const selectedByPlayers = players && players.filter(p => p.isTurn && !p.isGiver && p.input && p.input.selectedCards.find(c => c.word === word));

		const guessessThisTurn = thisTurn && thisTurn.guesses || [];
		const isGuessedThisTurn = guessessThisTurn.find(guess => guess.word === word) || null;

		let hasEnoughVotesToGuess = false;
		if (isAuthPlayerGuessing && isCorrectGuess === null) {
			const guessers = players.filter(p => p.isTurn && !p.isGiver && p.id !== authID);

			// if auth is the only guesser have them select card before showing guess button
			if (guessers.length === 0) {
				hasEnoughVotesToGuess = !!authPlayerInput.selectedCards.find(c => c.word === word);
			}
			else {
				const numVotes = guessers.reduce((r, p) => p.input.selectedCards.find(c => c.word === word) ? r + 1 : r, 0);
				const RATIO_NEEDED = 0.66; // 66%, 2/3

				// add + 1 because we excluded auth
				if ((numVotes + 1) / (guessers.length + 1) > RATIO_NEEDED) {
					hasEnoughVotesToGuess = true;
				}
			}
		}

		return {
			word, team, clue,
			isCorrectGuess, isGuessedThisTurn, isProcessingGuess,
			isSelectable, isSelected, isAuthTeam, isInteresting,
			hasEnoughVotesToGuess, selectedByPlayers,
			position: i
		};
	}) || [];

	$: thisTurn = game && game.turns && game.turns.length && game.turns[game.turns.length -1];
	$: numCardsSelected = (cards || []).filter(card => card.isSelected).length;
	$: numPlayersClickedRematch = (playersInput || []).filter(p => p.clickedRematch).length;
	$: clueNumber = !!thisTurn && thisTurn.action === 'guess' ? thisTurn.clue.clueNumber : numCardsSelected;
	$: currentGiver = !!thisTurn ? (thisTurn.team === TEAMS.one ? game.teamOne.giver : game.teamTwo.giver) : null;
	$: players = game && game.players && game.players.map(player => {
		const isCurrent = player.id === authID;
		const isGiver = game.teamOne.giver.id === player.id || game.teamTwo.giver.id === player.id;

		let isTurn = false;
		if (!gameOver && thisTurn) {
			if (thisTurn.team === player.team) {
				if (thisTurn.action === 'giveClue') {
					if (player.id === currentGiver.id) {
						isTurn = true;
					}
				}
				else if (thisTurn.action === 'guess') {
					if (player.id !== currentGiver.id) {
						isTurn = true;
					}
				}
			}
		}

		const input = playersInput.find(p => p.id === player.id);
		const isReadyToPlay = game.status >= STATUSES.finished && input && input.clickedRematch;

		return {
			...player,
			isCurrent,
			isGiver,
			isTurn,
			isReadyToPlay,
			input
		};
	}) || [];
	// $: players = [].concat(players).concat(players).concat(players).concat(players).concat(players).concat(players).concat(players).concat(players).concat(players).map(p => ({ ...p, id: Math.random(), isGiver:false, isCurrent: false })).concat(players);
	$: authPlayer = players.find(p => p.id === authID) || { name: '', team: '' };
	$: isAuthPlayerGivingClue = authPlayer.isTurn && thisTurn && thisTurn.action === 'giveClue';
	$: isAuthPlayerGuessing = authPlayer.isTurn && thisTurn && thisTurn.action === 'guess';
	$: selectedCardsThisTurn = players.filter(p => p.isTurn).reduce((r, p) => {
		if (isAuthPlayerGivingClue) {
			return r.concat(selectedCards || []);
		}
		else {
			return r.concat(p.input.selectedCards || []);
		}
	}, []);

	// trigger when it becomes player's turn again
	let lastAuthPlayerIsTurn = authPlayer && authPlayer.isTurn || null;
	$: if (authPlayer && authPlayer.isTurn !== lastAuthPlayerIsTurn) {
		lastAuthPlayerIsTurn = authPlayer.isTurn;
		if (authPlayer.isTurn) {
			pageTitle = "It's your turn!";
			if (document.visibilityState !== 'visible') {
				setTimeout(() => {
					alert("It's your turn");
				}, 1000);
			}
		}
		else {
			pageTitle = getPageTitle();
		}
	}

	let messageBit = 0;
	let message = { msg: '' };
	let messageTimeout;
	let lastMessageTurn = {};
	$: if (thisTurn || game.winningTeam || currentGiver || authPlayer) {
		const potentialMessage = getMessage() || getNextMessage();
		if (potentialMessage.msg !== lastMessageTurn.msg && (lastMessageTurn.turnIndex !== game.turns.length - 1 || lastMessageTurn.action !== thisTurn.action)) {
			lastMessageTurn = {
				turnIndex: game.turns.length - 1,
				action: thisTurn.action,
				msg: potentialMessage.msg
			};

			messageBit = messageBit ? 0 : 1;
			message = potentialMessage;

			const nextMessage = getNextMessage();
			if (message.msg !== nextMessage.msg) {
				isClueAreaLoading = false;
				clearTimeout(messageTimeout);
				messageTimeout = setTimeout(() => {
					message = nextMessage;
				}, 2000);
			}
		}
	}

	$: console.log('>>', message.msg);

	function getMessage() {
		if (game.winningTeam) {
			const teamName = game.winningTeam === TEAMS.one ? 'Blue' : 'Pink';
			return { msg: `${teamName} wins!`, className: game.winningTeam };
		}

		if (!thisTurn) {
			return { msg: ``, className: '' };
		}

		if (thisTurn.action === ACTIONS.guess) {
			const playerName = thisTurn.clue.player.name;
			const clueText = thisTurn.clue.clueText;

			if (!clueText) {
				return { msg: `${playerName} timed out`, className: thisTurn.team };
			}

			return { msg: `${playerName} gives clue: ${clueText} ${thisTurn.clue.clueNumber}`, className: thisTurn.team };
		}
		else if (game.turns.length >= 2) {
			const lastTurn = game.turns[game.turns.length - 2];
			const lastGuess = lastTurn.guesses[lastTurn.guesses.length - 1];
			const lastTeamName = lastTurn.team === TEAMS.one ? 'Blue' : 'Pink';

			if (!lastGuess.word) {
				return { msg: `${lastTeamName} passed`, className: lastTurn.team };
			}

			const rightWrong = lastGuess.isCorrect ? 'right' : 'wrong';

			return { msg: `${lastTeamName} guessed "${lastGuess.word}" ${rightWrong}`, className: lastTurn.team };
		}
		else if (game.turns.length <= 1) {
			const authTeamName = authPlayer.team === TEAMS.one ? 'Blue' : 'Pink';
			return { msg: `You are ${authTeamName}`, className: authPlayer.team };
		}
	}

	function getNextMessage() {
		if (game.winningTeam) {
			const teamName = game.winningTeam === TEAMS.one ? 'Blue' : 'Pink';
			return { msg: `${teamName} wins!`, className: game.winningTeam };
		}

		if (!thisTurn) {
			return { msg: ``, className: '' };
		}

		if (authPlayer.isTurn) {
			if (thisTurn.action === 'giveClue') {
				return { msg: `It's your turn to give a clue!`, className: thisTurn.team };
			}
			else {
				return { msg: `It's your turn to guess!`, className: thisTurn.team };
			}
		}
		else {
			if (thisTurn.action === 'giveClue') {
				return { msg: `${currentGiver.name} is thinking of a clue...`, className: thisTurn.team };
			}
			else {
				if (thisTurn.team === authPlayer.team) {
					return { msg: `Your team is guessing...`, className: thisTurn.team };
				}
				else {
					return { msg: `${thisTurn.team === TEAMS.one ? 'Blue' : 'Pink'} team is guessing...`, className: thisTurn.team };
				}
			}
		}
	}

	$: selectedCards = authPlayer && authPlayer.input && authPlayer.input.selectedCards || [];

	function toggleCardSelection({ word, position }) {
		if (!word || !authPlayer.isTurn) {
			return;
		}

		const selectedCard = selectedCards.find(card => card.word === word);

		if (selectedCard) {
			selectedCards = selectedCards.filter(card => card.word !== selectedCard.word);
		}
		else {
			selectedCards = [...selectedCards, { position, word }];
		}

		if (!authPlayer.isGiver) {
			gameRef.collection('playersInput').doc(authID).update({ selectedCards });
		}
	}

	let isClueAreaLoading = false;
	function onGiveClue(e) {
		const clueText = e.detail.clueText;
		const clueNumber = e.detail.clueNumber;
		isClueAreaLoading = true;

		giveClue({
			gameID: gameID,
			clueText,
			clueNumber
		}).then((result) => {
			isClueAreaLoading = false;
			// selectedCards = [];
			// console.log('###', result);
			// window.location.href = `./${result.data.gameID}`;
		})
		.catch(e => {
			console.error(e);
			isClueAreaLoading = false
		});

		document && document.activeElement && document.activeElement.blur();
	}

	function processGuess(e) {
		const { word, position } = e.detail;
		gameRef.collection('playersInput').doc(authID).update({ guessWords: arrayUnion(word) });
		document && document.activeElement && document.activeElement.blur();
		// console.log('!!!!', document && document.activeElement);
	}
	function endGuessing() {
		isClueAreaLoading = true; // this should become false when turn changes
		gameRef.collection('playersInput').doc(authID).update({ guessWords: arrayUnion('') });
	}

	function onCreateGame() {
		isClueAreaLoading = true;
		window.location.assign('./');
	}

	function onRematch() {
		gameRef.collection('playersInput').doc(authID).update({ clickedRematch: true });
	}

	function onCancelRematch() {
		gameRef.collection('playersInput').doc(authID).update({ clickedRematch: false });
	}

	let previousTotalPlayers;
	$: totalPlayers = (playersInput || []).filter(p => p.requestedTeam).length;
	$: if (totalPlayers !== previousTotalPlayers && !game.rematch) {
		previousTotalPlayers = totalPlayers;
		gameRef.collection('playersInput').doc(authID).update({ clickedRematch: false });
	}
	$: if (authPlayerInput && authPlayerInput.clickedRematch && numPlayersClickedRematch >= totalPlayers && game.rematch && game.rematch.gameID && game.rematch.mnemonic) {
		isClueAreaLoading = true;
		cards = null;
		const path = game.type === TYPES.pub ? game.rematch.gameID : game.rematch.mnemonic;
		window.location = './' + path;

		// g.setGameID(game.rematch.gameID).then(() => isClueAreaLoading = false);
	}

	onDestroy(() => {
		unGamesProtected && unGamesProtected();
	});

	$: lastPlayedMillis = thisTurn.timestampLastPlayed && thisTurn.timestampLastPlayed.seconds * 1000 + thisTurn.timestampLastPlayed.nanoseconds / 1000000 || 0;
	$: secondsToPlay = thisTurn.secondsToPlay;

	let secondsRemaining;

	updateSecondsRemaining();
	let interval;
	$: if (lastPlayedMillis && secondsToPlay && !gameOver) {
		updateSecondsRemaining();
		interval && clearInterval(interval);
		interval = setInterval(updateSecondsRemaining, 1000);
	}

	function updateSecondsRemaining() {
		const serverTime = Date.now() + serverTimeOffsetMillis;
		const diffSeconds = Math.round((serverTime - lastPlayedMillis) / 1000);
		secondsRemaining = Math.max(-1, secondsToPlay - diffSeconds);
		secondsRemaining = Math.min(secondsRemaining, secondsToPlay - 3); // we have a 3 second grace period (on server too) to show msg, etc
		if (secondsRemaining <= -1) {
			secondsRemaining = 0;
			interval && clearInterval(interval);
		}
	}

	function sendCheckTime() {
		isClueAreaLoading = true; // this should become false when turn changes
		gameRef.collection('playersInput').doc(authID).update({ checkTime: true });
	}

	let pageTitle = getPageTitle();
	function getPageTitle() {
		return `Careful Clues - ${game.mnemonic}`;
	}
</script>

<svelte:head>
	<title>{pageTitle}</title>
</svelte:head>

<Layout dimHeroImage={true}>
	<div slot="clue-left" class="clue-left">
		{#if game.timerLength && thisTurn.team === TEAMS.one && lastPlayedMillis && (secondsRemaining || secondsRemaining === 0) && !gameOver}
			<Clock {secondsRemaining} />
		{/if}
	</div>
	<div slot="clue-right" class="clue-right">
		{#if game.timerLength && thisTurn.team === TEAMS.two && lastPlayedMillis && (secondsRemaining || secondsRemaining === 0) && !gameOver}
			<Clock {secondsRemaining} />
		{/if}
	</div>

	<div slot="clue" class="clue-middle">
		<ClueArea
			showForm={isAuthPlayerGivingClue}
			{gameOver}
			isGuessing={isAuthPlayerGuessing}
			isLoading={isClueAreaLoading}
			clue={!!thisTurn && thisTurn.clue}
			team={!!thisTurn && thisTurn.team}
			{clueNumber}
			isJoinedPlayer={!!authPlayer && !!authPlayer.id}
			{numPlayersClickedRematch}
			authPlayerClickedRematch={authPlayerInput && authPlayerInput.clickedRematch}
			numPlayers={totalPlayers}
			showForceEndTurn={game.timerLength && secondsRemaining <= 0 && authPlayer.team !== thisTurn.team}
			on:createGame={onCreateGame}
			on:giveClue={onGiveClue}
			on:endGuessing={endGuessing}
			on:rematch={onRematch}
			on:cancelRematch={onCancelRematch}
			on:forceEndTurn={sendCheckTime}
		/>
	</div>

	<div class="team-list team-one" slot="team-one">
		<TeamList
			{players}
			team="one"
			cardsRemaining={game && game.teamOne && game.teamOne.cardsRemaining}
		/>
	</div>


	<div class="core" slot="core">
		{#if cards && cards.length}
			<Board
				cards={cards}
				showGuessButton={!gameOver && isAuthPlayerGuessing}
				isAuthGiver={authPlayer.isGiver}
				on:guess={processGuess}
				on:toggleCard={(e) => toggleCardSelection(e.detail)}
			/>
		{/if}
	</div>

	<div class="team-list team-two" slot="team-two">
		<TeamList
			{players}
			team="two"
			cardsRemaining={game && game.teamTwo && game.teamTwo.cardsRemaining}
		/>
	</div>

	<div
		class={`message-container ${message.className}`}
		class:highlight={authPlayer.isTurn && !gameOver}
		slot="msg"
	>
		<span class={messageBit ? 'animation1' : 'animation2' }>{message.msg}</span>
	</div>

	<!--
	<span class="footer" slot="footer" title="Share this link to invite your friends">
		game link: {window.location.href}
	</span>
	-->
</Layout>

<style lang="less">
	@import "./variables.less";

	.clue-left, .clue-right {
		width: 100%; max-width: 23vmin;
		@media (@aspect-ratio) {
			width: auto; max-width: unset;
		}
	}

	.clue-middle {
		min-height: 7.5em;
	}

	.team-list {
		display: flex;
		max-width: 25em; height: 100%; max-height: 100%;
		padding: 0.75vmin 0; margin: 0 0.5em;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		font-size: 1.5em;
		text-align: center;

		@media (@aspect-ratio) {
			justify-content: flex-start;
		}
	}

	.core {
		width: 100%; height: 100%;
	}

	.message-container {
		color: white;

		span {
			display: inline-block;
			min-height: 2em;
			padding: 0.5em 1em;
			font-size: 2.5em;
			border: 0.2em solid rgba(255,255,255,0.9);
			border-bottom: 0;
			border-top: 0;
			opacity: 0;
		}

		&.one {
			span {
				background: rgba(30,130,255, 1);
				&.animation1 {
					animation: large-sign-one1 3s ease-out 5ms forwards;
					.large-sign-keyframes(large-sign-one1, ~"30,130,255", 0.8);
				}
				&.animation2 {
					animation: large-sign-one 3s ease-out 5ms forwards;
					.large-sign-keyframes(large-sign-one, ~"30,130,255", 0.8);
				}
			}
		}
		&.two {
			span {
				background: rgba(255,0,204,0.7);

				&.animation1 {
					animation: large-sign-two1 3s ease-out 5ms forwards;
					.large-sign-keyframes(large-sign-two1, ~"255,0,204", 0.8);
				}
				&.animation2 {
					animation: large-sign-two 3s ease-out 5ms forwards;
					.large-sign-keyframes(large-sign-two, ~"255,0,204", 0.8);
				}

			}
		}
		&.highlight {
			animation: yellow-color 650ms infinite;
			animation-timing-function: ease-in;
			@keyframes yellow-color {
				0% {
					color: white;
				}
				50% {
					color: #ffff72;
				}
				100% {
					color: white;
				}
			}
		}

		.large-sign-keyframes(@name, @rgbFragment, @endAlpha) {
			@keyframes @name {
				0% {
					opacity: 1;
					top: -45vmin;
					transform: scale(2);
					background: rgba(@rgbFragment, 1);
					box-shadow: 0 0 0.5em rgba(0, 0, 0, 0.4);
				}
				90% {
					opacity: 1;
					top: -45vmin;
					transform: scale(2);
					background: rgba(@rgbFragment, 0.9);
					box-shadow: 0 0 0.5em rgba(0, 0, 0, 0.4);
				}
				100% {
					opacity: 1;
					top: 0;
					transform: scale(1);
					background: rgba(@rgbFragment, @endAlpha);
					box-shadow: none;
				}
			}
		}
/*
		&.turn {
			span {
				background: #15c029;
				box-shadow: 0 0 2em rgba(255, 255, 0, 0.5);

				animation: large-sign-green 3s ease-out 650ms forwards;
				.large-sign-keyframes(large-sign-green, ~"21,192,41", 1);
			}
		}
		*/
	}

</style>